import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Avatar,
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import PolicyIcon from "@mui/icons-material/Policy";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CircularProgress from "@mui/material/CircularProgress";
import PaymentsIcon from "@mui/icons-material/Payments";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { auth, uploadImage, updateUserProfile, syncPublicProfile } from "../firebase";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { endSession, isLoggedIn } from "../session";
import { useNavigate } from "react-router-dom";
import BottomNav from "./BottomNav";
import { brand } from "../theme";
import { PageShell, PageHeader, PageContent } from "./Page";

const menuItems = [
  { href: "/edit", icon: <SettingsIcon />, label: "Editar perfil", description: "Nombre, teléfono y localidad" },
  { href: "/myCars", icon: <DirectionsCarIcon />, label: "Mis vehículos", description: "Administrá tus publicaciones" },
  { href: "/membership", icon: <PaymentsIcon />, label: "Membresía", description: "7,90 USD/mes para publicar" },
  { href: "/support", icon: <SupportAgentIcon />, label: "Soporte", description: "Preguntas frecuentes y contacto" },
  { href: "/policy", icon: <PolicyIcon />, label: "Política y privacidad" },
  { href: "/terms", icon: <AutoStoriesIcon />, label: "Términos de servicio" },
];

const Perfil = () => {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null); // Estado para la URL de la imagen de perfil

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const { uid } = user;
          const db = getFirestore();
          const userDocRef = doc(db, "users", uid);

          const userDocSnapshot = await getDoc(userDocRef);

          if (userDocSnapshot.exists()) {
            const userData = userDocSnapshot.data();
            setEmail(userData.email || "");
            setDisplayName(userData.displayName || "Usuario");
            if (userData.profileImageUrl) {
              setProfileImageUrl(userData.profileImageUrl);
            }
            await syncPublicProfile(uid, {
              displayName: userData.displayName || "",
              photoURL: userData.profileImageUrl || "",
            });
          }
        } catch {
          // Error ignorado intencionalmente
        }
      } else {
        // Si el usuario cierra sesión, podemos limpiar el estado del displayName y de la imagen de perfil
        setDisplayName("");
        setEmail("");
        setProfileImageUrl(null);
      }
    });

    return unsubscribe;
  }, []);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    try {
      // Activar el indicador de progreso
      setLoading(true);

      // Subir la imagen al almacenamiento y obtener la URL de descarga
      const imageUrl = await uploadImage(file);

      // Obtener el usuario actual
      const user = auth.currentUser;
      if (user) {
        // Actualizar la URL de la imagen de perfil en la base de datos
        await updateUserProfile(user.uid, { profileImageUrl: imageUrl });

        // Actualizar el estado con la nueva URL de la imagen de perfil
        setProfileImageUrl(imageUrl);
      }
    } catch (error) {
      alert(error.message || "No se pudo subir la imagen.");
    } finally {
      setLoading(false);
    }
  };

  let navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate("/login");
    }
  }, [navigate]);

  const onLogout = () => {
    endSession();
    navigate("/login");
  };

  return (
    <PageShell>
      <PageHeader title="Perfil" subtitle="Tu cuenta en Caro" />
      <PageContent>
        <Stack spacing={3}>
          {/* Tarjeta de usuario */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              color: brand.cream,
              background: `linear-gradient(135deg, ${brand.ink} 0%, ${brand.inkLight} 100%)`,
              display: "flex",
              alignItems: "center",
              gap: { xs: 2, md: 3 },
            }}
          >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                alt={displayName}
                src={profileImageUrl || undefined}
                sx={{
                  width: { xs: 72, md: 96 },
                  height: { xs: 72, md: 96 },
                  fontSize: 36,
                  fontWeight: 800,
                  bgcolor: "secondary.main",
                  color: "secondary.contrastText",
                  border: `3px solid ${brand.cream}`,
                }}
              >
                {displayName.charAt(0).toUpperCase()}
              </Avatar>
              <input
                type="file"
                accept="image/*"
                onClick={(event) => (event.target.value = null)}
                onChange={handleImageUpload}
                style={{ display: "none" }}
                id="upload-button"
              />
              <IconButton
                aria-label="Cambiar foto de perfil"
                size="small"
                onClick={() => document.getElementById("upload-button").click()}
                disabled={loading}
                sx={{
                  position: "absolute",
                  right: -4,
                  bottom: -4,
                  bgcolor: "background.paper",
                  color: "primary.main",
                  boxShadow: 2,
                  "&:hover": { bgcolor: "background.paper" },
                }}
              >
                {loading ? <CircularProgress size={18} color="secondary" /> : <PhotoCameraIcon fontSize="small" />}
              </IconButton>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" noWrap>
                {displayName}
              </Typography>
              <Typography variant="body2" noWrap sx={{ opacity: 0.7 }}>
                {email}
              </Typography>
            </Box>
          </Paper>

          {/* Opciones de la cuenta */}
          <Paper elevation={0} sx={{ borderRadius: 4, border: 1, borderColor: "divider", overflow: "hidden" }}>
            <List disablePadding>
              {menuItems.map((item, index) => (
                <React.Fragment key={item.href}>
                  {index > 0 && <Divider component="li" />}
                  <ListItemButton href={item.href} sx={{ px: { xs: 2, md: 3 }, py: 1.75 }}>
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: 2,
                        width: 40,
                        height: 40,
                        borderRadius: 2.5,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "background.default",
                        color: "primary.main",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      secondary={item.description}
                      primaryTypographyProps={{ fontWeight: 700 }}
                    />
                    <ChevronRightIcon sx={{ color: "text.secondary" }} />
                  </ListItemButton>
                </React.Fragment>
              ))}
            </List>
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: 4, border: 1, borderColor: "divider", overflow: "hidden" }}>
            <ListItemButton onClick={onLogout} sx={{ px: { xs: 2, md: 3 }, py: 1.75, color: "error.main" }}>
              <ListItemIcon sx={{ minWidth: 0, mr: 2, width: 40, display: "grid", placeItems: "center", color: "inherit" }}>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText primary="Cerrar sesión" primaryTypographyProps={{ fontWeight: 700 }} />
            </ListItemButton>
          </Paper>
        </Stack>
      </PageContent>
      <BottomNav />
    </PageShell>
  );
};

export default Perfil;
